import RFQForm from "@/components/site/RFQForm";
import { RevealLines } from "@/components/site/RevealText";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";

const CONTACTS = [
  {
    key: "mj",
    name: "MJ Gill",
    role: "Director · Sourcing & Strategy",
    phone: "+971 50 800 3202",
    phoneRaw: "+971508003202",
    waNumber: "971508003202",
    email: "source@nexoriq.co",
  },
  {
    key: "priya",
    name: "Priya Gill",
    role: "Sourcing Lead",
    phone: "+91 78888 02673",
    phoneRaw: "+917888802673",
    waNumber: "917888802673",
    email: "source@nexoriq.co",
  },
];

const WHATSAPP_PREFILL =
  "Hi Nexoriq, I'd like to discuss a sourcing brief for [category]. When can we connect?";

export default function Contact() {
  return (
    <div data-testid="page-contact">
      <section className="px-6 md:px-10 pt-12 md:pt-20 pb-14 md:pb-20">
        <p className="font-mono text-[0.65rem] uppercase tracking-[0.24em] text-nx-steel2 mb-8">
          → Contact / RFQ — 06
        </p>
        <RevealLines
          as="h1"
          lines={[
            <span key="1">Send the spec.</span>,
            <span key="2" className="font-serif italic font-normal text-nx-navy2">Get an engineer.</span>,
          ]}
          className="font-cabinet font-black tracking-[-0.045em] text-nx-ink text-[15vw] md:text-[9vw] leading-[0.88]"
          stagger={0.15}
          mode="mount"
        />
        <p className="mt-8 max-w-2xl text-nx-navy2 text-base md:text-lg leading-relaxed">
          Fill the brief below or reach the desk directly. Express quotes returned within 48 hours; urgent scope handled inside the same day.
        </p>
      </section>

      <section className="px-6 md:px-10 pb-24 md:pb-32 grid grid-cols-12 gap-8 md:gap-14">
        {/* Left: contact block */}
        <aside className="col-span-12 md:col-span-4 space-y-10">
          {CONTACTS.map((c, i) => (
            <ContactPerson key={c.key} c={c} index={i} />
          ))}

          <div className="pt-8 border-t border-nx-navy/25 space-y-3">
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.24em] text-nx-steel2 mb-2">
              → Office
            </p>
            <div
              className="flex items-start gap-4"
              data-testid="contact-address"
            >
              <MapPin
                size={18}
                strokeWidth={1.6}
                className="mt-1 text-nx-steel2 shrink-0"
              />
              <div>
                <div className="font-mono text-[0.6rem] uppercase tracking-[0.24em] text-nx-steel2 mb-1.5">
                  Headquarters
                </div>
                <div className="text-nx-navy leading-snug">
                  VUNE2559 Compass Building
                  <br />
                  Al Hulaila Industrial Zone-FZ
                  <br />
                  P.O. Box 10055
                  <br />
                  Ras Al Khaimah, United Arab Emirates
                </div>
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-nx-navy/25 flex items-start gap-4">
            <Clock size={18} strokeWidth={1.6} className="mt-1 text-nx-steel2 shrink-0" />
            <div>
              <div className="font-mono text-[0.6rem] uppercase tracking-[0.24em] text-nx-steel2 mb-1.5">
                Response window
              </div>
              <div className="font-cabinet font-black text-2xl md:text-3xl tracking-tight text-nx-navy leading-none">
                Within 48 hours
              </div>
              <div className="mt-2 font-serif italic text-nx-navy2">Urgent · same day</div>
            </div>
          </div>

          <div className="pt-8 border-t border-nx-navy/25">
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.24em] text-nx-steel2 mb-4">
              → Time zones
            </p>
            <p className="font-serif italic text-nx-navy2 text-xl leading-snug max-w-xs">
              Operating across USA, Canada, EU, UAE, and East Asia hours — a real engineer, wherever your desk is.
            </p>
          </div>
        </aside>

        {/* Right: form */}
        <div className="col-span-12 md:col-span-8 border border-nx-navy p-6 md:p-10 bg-white">
          <div className="flex items-center justify-between mb-8">
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.24em] text-nx-steel2">
              → Request for Quote
            </p>
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.24em] text-nx-steel2">
              NX / RFQ / 01
            </p>
          </div>
          <RFQForm />
        </div>
      </section>

      {/* Map */}
      <section className="px-6 md:px-10 pb-24 md:pb-32" data-testid="contact-map-section">
        <p className="font-mono text-[0.65rem] uppercase tracking-[0.24em] text-nx-steel2 mb-4">
          → Find us
        </p>
        <h2 className="font-cabinet font-black tracking-tight text-nx-navy text-2xl md:text-4xl mb-8">
          Compass Building · Al Hulaila Industrial Zone-FZ · Ras Al Khaimah
        </h2>
        <div className="border border-nx-navy overflow-hidden">
          <iframe
            title="NEXORIQ — Compass Building, Al Hulaila Industrial Zone-FZ, Ras Al Khaimah"
            src="https://www.google.com/maps?q=Compass%20Building%2C%20Al%20Hulaila%20Industrial%20Zone%2C%20Ras%20Al%20Khaimah%2C%20United%20Arab%20Emirates&output=embed"
            className="w-full h-[380px] md:h-[480px] block"
            style={{ border: 0, filter: "grayscale(35%) contrast(105%)" }}
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="contact-map-iframe"
          />
        </div>
        <a
          href="https://www.google.com/maps/search/?api=1&query=Compass%20Building%2C%20Al%20Hulaila%20Industrial%20Zone%2C%20Ras%20Al%20Khaimah%2C%20United%20Arab%20Emirates"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-4 border-b border-nx-copper pb-0.5 uppercase tracking-[0.14em] text-[0.68rem] text-nx-copper hover:text-nx-navy transition-colors"
          data-testid="contact-map-directions"
        >
          <MapPin size={13} /> Open in Google Maps
        </a>
      </section>
    </div>
  );
}

function ContactPerson({ c, index }) {
  const waHref = `https://wa.me/${c.waNumber}?text=${encodeURIComponent(
    WHATSAPP_PREFILL,
  )}`;
  return (
    <div
      className="space-y-3"
      data-testid={`contact-person-${c.key}`}
    >
      <p className="font-mono text-[0.65rem] uppercase tracking-[0.24em] text-nx-steel2">
        → Contact 0{index + 1}
      </p>
      <div className="font-cabinet font-black text-2xl md:text-3xl tracking-tight text-nx-navy leading-none">
        {c.name}
      </div>
      <div className="font-serif italic text-nx-navy2">{c.role}</div>

      <div className="pt-3 space-y-2">
        <a
          href={`tel:${c.phoneRaw}`}
          data-testid={`contact-phone-${c.key}`}
          className="flex items-center gap-2 text-nx-navy hover:text-nx-accent transition-colors nx-underline text-sm"
        >
          <Phone size={14} strokeWidth={1.7} /> {c.phone}
        </a>
        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`contact-whatsapp-${c.key}`}
          className="flex items-center gap-2 text-nx-navy hover:text-nx-accent transition-colors nx-underline text-sm"
        >
          <MessageCircle size={14} strokeWidth={1.7} /> Chat on WhatsApp
        </a>
        <a
          href={`mailto:${c.email}`}
          data-testid={`contact-email-${c.key}`}
          className="flex items-center gap-2 text-nx-navy hover:text-nx-accent transition-colors nx-underline text-sm"
        >
          <Mail size={14} strokeWidth={1.7} /> {c.email}
        </a>
      </div>
    </div>
  );
}
